import type { ValueSelectorOption } from '@features/shared/components/ValueSelector'
import { isTimeZone, timeZoneLabel } from '@features/shared/utils/timeZones'
import { PRIMARY_TIME_ZONE_PREFERENCE_KEY } from './timeZoneKeys'
import type { PreferenceItem, PreferenceResolutionSource, ValueOption } from '../models'
import { getPreferenceValueOptions } from './preferences'

// Shared by every preference editor, so a value reads and edits the same on each page.

/** A value as text; `fallback` stands in for no value. */
export function formatValue(value: unknown, fallback = 'None') {
  if (value === null || value === undefined) return fallback
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'number' || typeof value === 'bigint') return String(value)
  if (typeof value === 'string') return value || fallback

  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

export function valuesEqual(left: unknown, right: unknown) {
  return JSON.stringify(left) === JSON.stringify(right)
}

/** A selector option's id for a value: a string is its own id, anything else its JSON. */
export function optionId(value: unknown) {
  return typeof value === 'string' ? value : JSON.stringify(value)
}

/** Whether a preference holds an address in the app, picked with the route picker. */
export function preferenceIsRoute(preference: PreferenceItem) {
  return preference.preference_key === 'home_page'
}

/** Whether a preference holds a time zone, picked with the time zone selector (Primary Time Zone). */
export function preferenceIsTimeZone(preference: PreferenceItem) {
  return preference.preference_key === PRIMARY_TIME_ZONE_PREFERENCE_KEY
}

/** Whether a preference holds a fixed `#RRGGBB` color, edited with a swatch and its hex code. */
export function preferenceIsColor(preference: PreferenceItem) {
  return preference.value_app_data_type_name === 'color'
}

/**
 * Preferences picked from a few short options side by side: Theme
 * ([Light][Dark][System]) and the sidebar's Expansion, which the sidebar's
 * foot offers too, the sidebar's sort methods ([Name][Recency][Custom]), and
 * the Export tab's layouts and choices, as the export Customize dialog has them.
 */
const SEGMENTED_PREFERENCE_KEYS: ReadonlySet<string> = new Set([
  'theme',
  'sidebar_navigation_mode',
  'sidebar_table_sort',
  'sidebar_favorites_sort',
  'export_record_level',
  'export_csv_layout',
  'export_csv_group_levels',
  'export_csv_level_column',
  'export_csv_values',
  'export_csv_delimiter',
  'export_excel_layout',
  'export_excel_group_levels',
  'export_excel_level_column',
  'export_excel_totals_as',
  'export_excel_group_totals_position',
  'export_excel_column_widths',
  'export_pdf_layout',
  'export_pdf_group_levels',
  'export_pdf_level_column',
  'export_pdf_orientation',
  'export_pdf_margins',
  'export_pdf_density',
  'export_pdf_width',
  'export_pdf_column_widths',
  'export_pdf_long_text',
  'export_pdf_page_order',
  'export_pdf_repeat_columns',
  'export_pdf_page_breaks',
  'export_pdf_grid_lines',
])

/** The most options a segmented control lays out; more belong in a select. */
const MAX_SEGMENTS = 4

/** Whether a preference meant for segments has two to four options to lay out. */
export function preferenceIsSegmented(preference: PreferenceItem) {
  const optionCount = getPreferenceValueOptions(preference).length
  return (
    SEGMENTED_PREFERENCE_KEYS.has(preference.preference_key) &&
    optionCount >= 2 &&
    optionCount <= MAX_SEGMENTS
  )
}

/**
 * Two-option preferences that read as on or off, edited with a switch: the
 * value each side of it stores. "Pin sidebar sub-menu" is on for `pinned`.
 */
const SWITCH_VALUES: Partial<Record<string, { on: string; off: string }>> = {
  sidebar_menu_mode: { on: 'pinned', off: 'hover' },
}

/** The values a two-option preference's switch stores, when it has both as options. */
function switchValues(preference: PreferenceItem) {
  const values = SWITCH_VALUES[preference.preference_key]
  if (!values) return null
  const ids = new Set(getPreferenceValueOptions(preference).map((option) => optionId(option.value)))
  return ids.size === 2 && ids.has(values.on) && ids.has(values.off) ? values : null
}

/**
 * Edits as a toggle switch: a non-nullable boolean (a nullable one keeps the
 * Yes/No selector), or a two-option preference that reads as on or off.
 */
export function preferenceIsToggle(preference: PreferenceItem) {
  if (switchValues(preference)) return true
  return (
    preference.value_app_data_type_name === 'boolean' &&
    !preference.value_is_nullable &&
    getPreferenceValueOptions(preference).length === 0
  )
}

/** Whether a toggle preference's value is its on side. */
export function preferenceToggleIsOn(preference: PreferenceItem, value: unknown) {
  const values = switchValues(preference)
  return values ? value === values.on : value === true
}

/** The value a toggle preference stores for on or off. */
export function preferenceToggleValue(preference: PreferenceItem, on: boolean): unknown {
  const values = switchValues(preference)
  if (!values) return on
  return on ? values.on : values.off
}

/** A preference shown on one line, label and control side by side: a switch or segments. */
export function preferenceIsInline(preference: PreferenceItem) {
  return preferenceIsToggle(preference) || preferenceIsSegmented(preference)
}

/** A preference's value as text: On or Off for a toggle, an option's label for an option. */
export function formatPreferenceValue(preference: PreferenceItem, value: unknown) {
  if (preferenceIsTimeZone(preference) && isTimeZone(value)) return timeZoneLabel(value)
  if (preferenceIsToggle(preference) && (typeof value === 'boolean' || switchValues(preference))) {
    return preferenceToggleIsOn(preference, value) ? 'On' : 'Off'
  }
  const id = optionId(value)
  const option = getPreferenceValueOptions(preference).find((item) => optionId(item.value) === id)
  return option ? option.label : formatValue(value)
}

/**
 * Whose default a group or user without its own value follows, to read
 * "Reset to JBPro default": the organization by its casual name, or the user's
 * group. Mid-sentence casing; capitalize it to start one.
 */
export function preferenceDefaultOwner(
  preference: PreferenceItem,
  organizationCasualName: string | null
) {
  if (preference.inherited_source === 'group_assignment') return "your group's"
  return organizationCasualName?.trim() || "the organization's"
}

/** A preference's configured options, or Yes and No for a boolean; empty when typed or shown. */
export function preferenceSelectorValueOptions(preference: PreferenceItem): ValueOption[] {
  const configuredOptions = getPreferenceValueOptions(preference)
  if (configuredOptions.length > 0) return configuredOptions
  if (preference.value_app_data_type_name === 'boolean') {
    return [
      { label: 'Yes', value: true },
      { label: 'No', value: false },
    ]
  }
  return []
}

/** Selector options, plus the current value when it isn't one of them. */
export function preferenceSelectorOptions(
  valueOptions: ValueOption[],
  currentValue: unknown
): ValueSelectorOption[] {
  const options = valueOptions.map((option) => ({
    id: optionId(option.value),
    label: option.label,
  }))
  const currentId = optionId(currentValue)
  return options.some((option) => option.id === currentId)
    ? options
    : [...options, { id: currentId, label: formatValue(currentValue) }]
}

export type PreferenceInputType = 'number' | 'date' | 'time' | 'text'

const TEXT_DATA_TYPES = new Set(['text', 'email', 'phone_number', 'url'])

/** The input a preference's value is typed into, or null for one that's only shown. */
export function preferenceInputType(preference: PreferenceItem): PreferenceInputType | null {
  const dataType = preference.value_app_data_type_name
  if (dataType === 'number' || dataType === 'date' || dataType === 'time') return dataType
  return TEXT_DATA_TYPES.has(dataType) ? 'text' : null
}

/** What an input of `type` shows for a stored value. */
export function preferenceInputValue(type: PreferenceInputType, value: unknown): string | number {
  if (type === 'number') {
    return typeof value === 'number' || typeof value === 'string' ? value : ''
  }
  if (typeof value !== 'string') return ''
  if (type === 'date') return value.slice(0, 10)
  // Stored as HH:MM:SS; the input edits HH:MM.
  if (type === 'time') return value.slice(0, 5)
  return value
}

/**
 * The stored value for an input's text, or `undefined` when an emptied number,
 * date or time input means the inherited value again. Text keeps an empty string.
 */
export function preferenceValueFromInput(type: PreferenceInputType, text: string): unknown {
  if (type === 'text') return text
  if (text === '') return undefined
  if (type === 'number') return Number(text)
  if (type === 'time') return `${text}:00`
  return text
}

/** The preference with its own value set at the edited level. */
export function preferenceDraftWithValue<TPreference extends PreferenceItem>(
  preference: TPreference,
  value: unknown,
  resolvedSource: PreferenceResolutionSource
): TPreference {
  return {
    ...preference,
    assigned_value: value,
    has_assignment: true,
    resolved_value: value,
    resolved_source: resolvedSource,
  }
}

/** The preference without a value of its own, resolving to the inherited value again. */
export function preferenceDraftWithoutAssignment<TPreference extends PreferenceItem>(
  preference: TPreference
): TPreference {
  return {
    ...preference,
    assignment_id: null,
    assigned_value: null,
    has_assignment: false,
    resolved_value: preference.inherited_value,
    resolved_source: preference.inherited_source,
  }
}

/**
 * Whether a group or user follows the default above it: no value of its own,
 * or one that matches the default (kept from before, and dropped on the next
 * save). Only a value that differs is the level's own choice, with a reset.
 */
export function preferenceFollowsDefault(preference: PreferenceItem) {
  return (
    !preference.has_assignment || valuesEqual(preference.assigned_value, preference.inherited_value)
  )
}

/**
 * The preference with `value` chosen at a group's or user's level. Choosing
 * the default above it follows that default rather than copying it, so a
 * later change to the default still reaches it.
 */
export function preferenceDraftWithChoice<TPreference extends PreferenceItem>(
  preference: TPreference,
  value: unknown,
  resolvedSource: PreferenceResolutionSource
): TPreference {
  return valuesEqual(value, preference.inherited_value)
    ? preferenceDraftWithoutAssignment(preference)
    : preferenceDraftWithValue(preference, value, resolvedSource)
}

/**
 * Whether a draft's choice differs from the saved one as the user sees it:
 * following the default (a matching value of its own included) is one choice,
 * each value of its own another. Nothing to save when only a matching copy
 * would go.
 */
export function preferenceChoiceChanged(original: PreferenceItem, draft: PreferenceItem) {
  const originalFollows = preferenceFollowsDefault(original)
  const draftFollows = preferenceFollowsDefault(draft)
  if (originalFollows || draftFollows) return originalFollows !== draftFollows
  return !valuesEqual(original.assigned_value, draft.assigned_value)
}

/** The value a save sends for a choice: null to follow the default, else its own. */
export function preferenceChoiceValue(preference: PreferenceItem): unknown {
  return preferenceFollowsDefault(preference) ? null : preference.assigned_value
}

/** A value of the level's own that only copies the default, which a save drops. */
export function preferenceHasDefaultCopy(preference: PreferenceItem) {
  return preference.has_assignment && preferenceFollowsDefault(preference)
}
